import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import Turnos from './pages/Turnos';
import Pacientes from './pages/Pacientes';
import Historial from './pages/Historial';
import Calendario from './pages/Calendario';
import Medicos from './pages/Medicos';
import Especialidades from './pages/Especialidades';
import Staff from './pages/Staff';
import Login from './pages/Login';
import MisDatos from './pages/MisDatos';
import PanelPaciente from './pages/PanelPaciente';
import Configuracion from './pages/Configuracion';
import BusquedaHC from './pages/BusquedaHC';
import Consulta from './pages/Consulta';
import ObrasSociales from './pages/ObrasSociales';
import Turnero from './pages/Turnero';
import ConfirmarAsistencia from './pages/ConfirmarAsistencia';
import Confirmaciones from './pages/Confirmaciones';
import Cajas from './pages/Cajas';
import DesignCompare from './pages/DesignCompare';
import AuditoriaTurnos from './pages/AuditoriaTurnos';
import AuditoriaMedicos from './pages/AuditoriaMedicos';
import ManualUsuario from './pages/ManualUsuario';
import ArticuloManual from './pages/ArticuloManual';
import GlosarioManual from './pages/GlosarioManual';
import FaqManual from './pages/FaqManual';
import Layout from './components/Layout';

const API = process.env.REACT_APP_API_URL || '';

const TEMA_DEFECTO = {
  color_fondo:            '#1a1a2e',
  color_superficie:       '#16213e',
  color_primario:         '#0f3460',
  color_acento:           '#90caf9',
  color_campo:            '#2a5aaa',
  color_texto:            '#e0e0e0',
  color_texto_secundario: '#90a4ae',
};

function RutaProtegida({ usuario, rolesPermitidos, children }) {
  if (!usuario) return <Navigate to="/login" replace />;
  if (rolesPermitidos && !rolesPermitidos.includes(usuario.rol)) {
    return <Navigate to="/sin-acceso" replace />;
  }
  return children;
}

function rutaInicio(usuario) {
  if (!usuario) return '/login';
  if (usuario.rol === 'paciente') {
    return usuario.datos_confirmados ? '/panel-paciente' : '/mis-datos';
  }
  return '/calendario';
}

const INACTIVIDAD_MS = 18 * 60 * 1000;

function App() {
  const [usuario, setUsuario]                     = useState(null);
  const [cargando, setCargando]                   = useState(true);
  const [avisoInactividad, setAvisoInactividad]   = useState(false);
  const [countdown, setCountdown]                 = useState(120);
  const timerInactividad = useRef(null);
  const timerCountdown   = useRef(null);

  // ── MUI theme estático con paleta por defecto ─────────────────────────────
  const muiTema = createTheme({
    palette: {
      mode: 'dark',
      background: {
        default: TEMA_DEFECTO.color_fondo,
        paper:   TEMA_DEFECTO.color_superficie,
      },
      text: {
        primary:   TEMA_DEFECTO.color_texto,
        secondary: TEMA_DEFECTO.color_texto_secundario,
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: { color: '#ffffff' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: TEMA_DEFECTO.color_campo,
            '&:hover':        { backgroundColor: TEMA_DEFECTO.color_campo },
            '&.Mui-focused':  { backgroundColor: TEMA_DEFECTO.color_campo },
            '&.Mui-disabled': { backgroundColor: TEMA_DEFECTO.color_campo, opacity: 0.5 },
          },
        },
      },
    },
  });

  // ── Aplica CSS variables del tema por defecto al arrancar ────────────────
  const applyDefaultTheme = useCallback(() => {
    const root = document.documentElement;
    root.style.setProperty('--c-fondo',      TEMA_DEFECTO.color_fondo);
    root.style.setProperty('--c-superficie', TEMA_DEFECTO.color_superficie);
    root.style.setProperty('--c-primario',   TEMA_DEFECTO.color_primario);
    root.style.setProperty('--c-acento',     TEMA_DEFECTO.color_acento);
    root.style.setProperty('--c-campo',      TEMA_DEFECTO.color_campo);
    root.style.setProperty('--c-texto',      TEMA_DEFECTO.color_texto);
    root.style.setProperty('--c-texto-sec',  TEMA_DEFECTO.color_texto_secundario);
  }, []);

  useEffect(() => {
    applyDefaultTheme();

    const token  = sessionStorage.getItem('token');
    const rol    = sessionStorage.getItem('rol');
    const nombre = sessionStorage.getItem('nombre');
    const expira = sessionStorage.getItem('expira');
    const datos_confirmados = sessionStorage.getItem('datos_confirmados') === '1';
    const debe_cambiar_password = sessionStorage.getItem('debe_cambiar_password') === '1';

    if (token && rol && nombre && expira) {
      if (Date.now() < parseInt(expira)) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUsuario({ token, rol, nombre, datos_confirmados, debe_cambiar_password });
      } else {
        sessionStorage.clear();
      }
    }
    setCargando(false);
  }, []); // eslint-disable-line

  // Ping de actividad cada 4 minutos
  useEffect(() => {
    if (!usuario || usuario.rol === 'paciente') return;
    const ping = () => axios.post(`${API}/api/auth/ping`).catch(() => {});
    ping();
    const iv = setInterval(ping, 4 * 60 * 1000);
    return () => clearInterval(iv);
  }, [usuario]);

  // ── Timeout de inactividad ────────────────────────────────────────────────
  const reiniciarTimer = useCallback(() => {
    if (!usuario || usuario.rol === 'paciente') return;
    clearTimeout(timerInactividad.current);
    clearInterval(timerCountdown.current);
    setAvisoInactividad(false);
    timerInactividad.current = setTimeout(() => {
      setCountdown(120);
      setAvisoInactividad(true);
      timerCountdown.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timerCountdown.current);
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVIDAD_MS);
  }, [usuario]); // eslint-disable-line

  useEffect(() => {
    if (!usuario || usuario.rol === 'paciente') return;
    const eventos = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    eventos.forEach(e => window.addEventListener(e, reiniciarTimer, { passive: true }));
    reiniciarTimer();
    return () => {
      eventos.forEach(e => window.removeEventListener(e, reiniciarTimer));
      clearTimeout(timerInactividad.current);
      clearInterval(timerCountdown.current);
    };
  }, [usuario, reiniciarTimer]);

  function handleLogin(data) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUsuario({
      token: data.token,
      rol:   data.rol,
      nombre: data.nombre,
      datos_confirmados: !!data.datos_confirmados,
      debe_cambiar_password: !!data.debe_cambiar_password,
    });
  }

  function handlePasswordCambiada() {
    sessionStorage.setItem('debe_cambiar_password', '0');
    setUsuario(prev => ({ ...prev, debe_cambiar_password: false }));
  }

  function handleLogout() {
    delete axios.defaults.headers.common['Authorization'];
    sessionStorage.clear();
    applyDefaultTheme();
    clearTimeout(timerInactividad.current);
    clearInterval(timerCountdown.current);
    setAvisoInactividad(false);
    setUsuario(null);
  }

  function handleDatosConfirmados() {
    sessionStorage.setItem('datos_confirmados', '1');
    setUsuario(prev => ({ ...prev, datos_confirmados: true }));
  }

  if (cargando) return null;

  const minutos  = String(Math.floor(countdown / 60)).padStart(2, '0');
  const segundos = String(countdown % 60).padStart(2, '0');

  return (
    <ThemeProvider theme={muiTema}>
      <BrowserRouter>
        <Routes>
          <Route path="/design-compare" element={<DesignCompare />} />
          <Route path="/turnero" element={<Turnero />} />
          <Route path="/confirmar" element={<ConfirmarAsistencia />} />
          <Route path="/login" element={
            usuario ? <Navigate to={rutaInicio(usuario)} replace /> : <Login onLogin={handleLogin} />
          } />
          <Route path="/sin-acceso" element={
            <div style={{ padding: 40, color: '#fff' }}>No tenés acceso a esta sección.</div>
          } />
          <Route path="/*" element={
            <RutaProtegida usuario={usuario}>
              <Layout usuario={usuario} onLogout={handleLogout} onPasswordCambiada={handlePasswordCambiada}>
                <Routes>
                  <Route path="/" element={<Navigate to={rutaInicio(usuario)} replace />} />

                  {/* Paciente */}
                  <Route path="/mis-datos" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['paciente']}>
                      <MisDatos usuario={usuario} onConfirmado={handleDatosConfirmados} />
                    </RutaProtegida>
                  } />
                  <Route path="/panel-paciente" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['paciente']}>
                      <PanelPaciente usuario={usuario} />
                    </RutaProtegida>
                  } />

                  {/* Staff */}
                  <Route path="/calendario" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista', 'medico']}>
                      <Calendario />
                    </RutaProtegida>
                  } />
                  <Route path="/turnos" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista', 'medico']}>
                      <Turnos />
                    </RutaProtegida>
                  } />
                  <Route path="/pacientes" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista']}>
                      <Pacientes />
                    </RutaProtegida>
                  } />
                  <Route path="/confirmaciones" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista']}>
                      <Confirmaciones />
                    </RutaProtegida>
                  } />
                  <Route path="/historial/:id" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'medico']}>
                      <Historial />
                    </RutaProtegida>
                  } />
                  <Route path="/consulta/:turnoId" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'medico']}>
                      <Consulta />
                    </RutaProtegida>
                  } />
                  <Route path="/busqueda-hc" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'medico']}>
                      <BusquedaHC />
                    </RutaProtegida>
                  } />
                  <Route path="/medicos" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista']}>
                      <Medicos />
                    </RutaProtegida>
                  } />
                  <Route path="/especialidades" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista']}>
                      <Especialidades />
                    </RutaProtegida>
                  } />
                  <Route path="/obras-sociales" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista']}>
                      <ObrasSociales />
                    </RutaProtegida>
                  } />
                  <Route path="/staff" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin']}>
                      <Staff />
                    </RutaProtegida>
                  } />
                  <Route path="/configuracion" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin']}>
                      <Configuracion />
                    </RutaProtegida>
                  } />
                  <Route path="/caja" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin', 'recepcionista', 'medico']}>
                      <Cajas usuario={usuario} />
                    </RutaProtegida>
                  } />
                  <Route path="/auditoria-turnos" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin']}>
                      <AuditoriaTurnos />
                    </RutaProtegida>
                  } />
                  <Route path="/auditoria-medicos" element={
                    <RutaProtegida usuario={usuario} rolesPermitidos={['admin']}>
                      <AuditoriaMedicos />
                    </RutaProtegida>
                  } />

                  {/* Manual de Usuario — accesible por todos los roles */}
                  <Route path="/manual" element={<ManualUsuario />} />
                  <Route path="/manual/glosario" element={<GlosarioManual />} />
                  <Route path="/manual/faq" element={<FaqManual />} />
                  <Route path="/manual/:articuloId" element={<ArticuloManual />} />
                </Routes>
              </Layout>
            </RutaProtegida>
          } />
        </Routes>

        {/* Modal de inactividad — colores fijos, no depende del tema */}
        <Dialog open={avisoInactividad} maxWidth="xs" fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid #e91e63', borderRadius: 2, textAlign: 'center' } }}>
          <DialogContent sx={{ pt: 4, pb: 2 }}>
            <Typography sx={{ fontSize: 40, mb: 1 }}>⏱️</Typography>
            <Typography variant="h6" sx={{ color: '#f48fb1', fontWeight: 700, mb: 1 }}>
              Tu sesión está por vencer
            </Typography>
            <Typography sx={{ color: '#b0b0b0', fontSize: 14, mb: 3 }}>
              Por inactividad, el sistema va a cerrar tu sesión en:
            </Typography>
            <Box sx={{ bgcolor: '#0f0f1a', borderRadius: 2, py: 2, px: 4, display: 'inline-block', mb: 1 }}>
              <Typography sx={{ fontSize: 48, fontWeight: 700, color: countdown <= 30 ? '#f44336' : '#f48fb1', fontFamily: 'monospace', letterSpacing: 4 }}>
                {minutos}:{segundos}
              </Typography>
            </Box>
            <Typography sx={{ color: '#666', fontSize: 12, mt: 1 }}>
              Si seguís trabajando, hacé clic en el botón.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
            <Button variant="contained" size="large" onClick={reiniciarTimer}
              sx={{ bgcolor: '#e91e63', '&:hover': { bgcolor: '#c2185b' }, px: 4, fontWeight: 700 }}>
              Sí, continuar trabajando
            </Button>
            <Button variant="outlined" size="large" onClick={handleLogout}
              sx={{ color: '#b0b0b0', borderColor: '#444', px: 3 }}>
              Cerrar sesión
            </Button>
          </DialogActions>
        </Dialog>
      </BrowserRouter>

    </ThemeProvider>
  );
}

export default App;
